import React from 'react';
import { Button } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import { LoadingSpinner } from 'components/LoadingSpinner/LoadingSpinner';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useLocale } from 'hooks/useLocale';
import { TSupportedProductTypes } from 'lendingApi/applications.types';
import {
  FLEXI_LOAN_BOUNDS_DE,
  FLEXI_LOAN_BOUNDS_UK,
  getFormattedProductBounds,
} from 'utils/productBounds';
import { getUnlockURL } from './getUnlockURLHelper';
import { NoProducts } from './NoProducts';
import styles from './ProductItems.module.css';
import { Flexi24 } from './products/Flexi24';
import { FlexiLoan } from './products/FlexiLoan';
import { InterestOnlyFlexiLoan } from './products/InterestOnlyFlexiLoan';
import { IwocaPay } from './products/IwocaPay';
import { LongerFlexiLoan } from './products/LongerFlexiLoan';

export const ProductItems = ({
  applyForProduct,
}: {
  applyForProduct: (product: TSupportedProductTypes) => void;
}) => {
  const { isUK, isDE, locale } = useLocale();
  const {
    introducerStatus,
    checkIntroducerPermission,
    availableProducts,
    introducerCompany,
    introducerType,
    splitTests,
  } = useIntroducerContext();
  const hasFlexiLoan = availableProducts?.includes('credit_facility');

  const formattedBoundsFlexiLoanUK = getFormattedProductBounds(
    FLEXI_LOAN_BOUNDS_UK,
    locale,
  );
  const formattedBoundsFlexiLoanDE = getFormattedProductBounds(
    FLEXI_LOAN_BOUNDS_DE,
    locale,
  );

  if (!introducerType) {
    return <LoadingSpinner />;
  }

  if (
    isDE &&
    introducerStatus === 'success' &&
    (introducerCompany?.available_products || []).length === 0
  ) {
    return <NoProducts />;
  }

  const renderProducts = () => {
    const products = [
      <div key="flexiloan" className={styles.productContainer}>
        <FlexiLoan />
      </div>,
    ];

    if (splitTests?.accessToInterestOnlyLoans) {
      products.push(
        <div key="interestOnlyflexiloan" className={styles.productContainer}>
          <InterestOnlyFlexiLoan />
        </div>,
      );
    }

    if (isUK) {
      products.push(
        <div className={styles.productContainer}>
          <Flexi24 />
        </div>,
      );

      products.push(
        <div className={styles.productContainer}>
          <LongerFlexiLoan />
        </div>,
      );
    }
    return products.map((product, index) => (
      <React.Fragment key={index}>{product}</React.Fragment>
    ));
  };

  const renderAction = () => {
    if (hasFlexiLoan) {
      return (
        <Button
          variant="primary"
          size="small"
          className={styles.ctaButton}
          disabled={!checkIntroducerPermission('can_create_applications')}
          onClick={() => applyForProduct('credit_facility')}
        >
          <Trans context="product-items">Apply</Trans>
        </Button>
      );
    }

    return (
      <Button
        className={clsx('linkButton', styles.ctaButton)}
        variant="denim"
        size="small"
        href={getUnlockURL('credit_facility', introducerType)}
        target="_blank"
        rel="noreferrer"
      >
        Unlock
      </Button>
    );
  };

  const renderSubheading = () => (
    <>
      <Trans>Loans from</Trans>{' '}
      {isDE ? (
        <Trans>
          {formattedBoundsFlexiLoanDE.min} to {formattedBoundsFlexiLoanDE.max}
        </Trans>
      ) : (
        <Trans>
          {formattedBoundsFlexiLoanUK.min} to {formattedBoundsFlexiLoanUK.max}
        </Trans>
      )}
    </>
  );

  return (
    <div data-testid="product-items">
      <LayoutHeadings
        heading={t`Available Flexi-Loans`}
        subHeading={renderSubheading()}
        headingAlignment="center"
      />
      <div className={styles.products}>{renderProducts()}</div>
      {renderAction()}
      {(introducerType === 'accountant' ||
        availableProducts?.includes('iwocapay')) && (
        <div
          className={clsx(styles.productContainer, styles.iwocaPayContainer)}
        >
          <IwocaPay unlockURL={getUnlockURL('iwocaPay', introducerType)} />
        </div>
      )}
    </div>
  );
};
